<template>
    <div class="ind-settings-overlay" @mousedown.self="onCancel">
        <div class="ind-settings-dialog" ref="dialog" :style="popoverStyle" role="dialog" :aria-label="ovName + ' properties'">
            <!-- Header -->
            <div class="ind-settings-header">
                <h2 class="ind-settings-title">{{ ovName }}</h2>
                <button class="ind-settings-close" @click="onCancel">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                </button>
            </div>

            <!-- Tabs -->
            <div class="ind-settings-tabs">
                <button
                    v-for="tab in tabs"
                    :key="tab.id"
                    :class="['ind-tab-btn', { active: activeTab === tab.id }]"
                    @click="activeTab = tab.id">
                    {{ tab.label }}
                </button>
            </div>
            <div class="ind-tab-line">
                <div class="ind-tab-indicator" :style="tabIndicatorStyle"></div>
            </div>

            <!-- Tab Content -->
            <div class="ind-settings-body">
                <p v-if="ov && ov.executionError" role="status" class="ind-execution-error">{{ ov.executionError }}</p>
                <!-- Inputs Tab -->
                <div v-show="activeTab === 'inputs'" class="ind-tab-content">
                    <div v-if="hasTimeframeTrend" ref="timeframeTrendProperties" class="timeframe-trend-properties-host"></div>
                    <div v-if="!inputFields.length && !hasTimeframeTrend" class="ind-empty-state">
                        <i>No configurable inputs</i>
                    </div>
                    <template v-if="isGroup">
                        <template v-for="(field, fi) in inputFields" :key="field.key">
                            <div v-if="fi === 0 || inputFields[fi - 1]._ovIndex !== field._ovIndex"

                                class="ind-group-section-header">
                                {{ field._ovName }}
                            </div>
                            <div class="ind-input-row">
                                <template v-if="field.type === 'boolean'">
                                    <label class="ind-checkbox-row">
                                        <input type="checkbox"
                                            :checked="localSettings[field.key]"
                                            @change="onInputChange(field.key, $event.target.checked)"
                                            class="ind-checkbox" />
                                        <span class="ind-checkbox-label">{{ field.label }}</span>
                                    </label>
                                </template>
                                <template v-else-if="field.type === 'number'">
                                    <label class="ind-field-label">{{ field.label }}</label>
                                    <input type="number"
                                        :value="localSettings[field.key]"
                                        :min="field.min" :max="field.max" :step="field.step || 1"
                                        class="ind-text-input"
                                        @input="onInputChange(field.key, parseNum($event.target.value))" />
                                </template>
                                <template v-else-if="field.type === 'select'">
                                    <label class="ind-field-label">{{ field.label }}</label>
                                    <select :value="localSettings[field.key]"
                                        class="ind-select-input"
                                        @change="onInputChange(field.key, $event.target.value)">
                                        <option v-for="opt in field.choices" :key="typeof opt==='object'?opt.value:opt" :value="typeof opt==='object'?opt.value:opt">{{ typeof opt==='object'?opt.label:opt }}</option>
                                    </select>
                                </template>
                                <template v-else-if="field.type === 'color'">
                                    <label class="ind-field-label">{{ field.label }}</label>
                                    <div class="ind-color-wrapper">
                                        <input type="color" :value="localSettings[field.key]"
                                            class="ind-color-input"
                                            @input="onInputChange(field.key, $event.target.value)" />
                                        <span class="ind-color-hex">{{ localSettings[field.key] }}</span>
                                    </div>
                                </template>
                                <template v-else-if="field.type === 'date'">
                                    <label class="ind-field-label">{{ field.label }}</label>
                                    <input type="date" :value="localSettings[field.key]"
                                        class="ind-text-input"
                                        @input="onInputChange(field.key, $event.target.value)" />
                                </template>
                                <template v-else>
                                    <label class="ind-field-label">{{ field.label }}</label>
                                    <input type="text" :value="localSettings[field.key]"
                                        class="ind-text-input"
                                        @input="onInputChange(field.key, $event.target.value)" />
                                </template>
                            </div>
                        </template>
                    </template>
                    <template v-else>
                        <div v-for="field in inputFields" :key="field.key" class="ind-input-row">
                        <!-- Checkbox / Boolean -->
                        <template v-if="field.type === 'boolean'">
                            <label class="ind-checkbox-row">
                                <input
                                    type="checkbox"
                                    :checked="localSettings[field.key]"
                                    @change="onInputChange(field.key, $event.target.checked)"
                                    class="ind-checkbox"
                                />
                                <span class="ind-checkbox-label">{{ field.label }}</span>
                            </label>
                        </template>

                        <!-- Number -->
                        <template v-else-if="field.type === 'number'">
                            <label class="ind-field-label">{{ field.label }}</label>
                            <input
                                type="number"
                                :value="localSettings[field.key]"
                                :min="field.min"
                                :max="field.max"
                                :step="field.step || 1"
                                class="ind-text-input"
                                @input="onInputChange(field.key, parseNum($event.target.value))"
                            />
                        </template>

                        <!-- Select / Combo -->
                        <template v-else-if="field.type === 'select'">
                            <label class="ind-field-label">{{ field.label }}</label>
                            <select
                                :value="localSettings[field.key]"
                                class="ind-select-input"
                                @change="onInputChange(field.key, $event.target.value)">
                                <option v-for="opt in field.choices" :key="typeof opt==='object'?opt.value:opt" :value="typeof opt==='object'?opt.value:opt">
                                    {{ typeof opt==='object'?opt.label:opt }}
                                </option>
                            </select>
                        </template>

                        <!-- Color -->
                        <template v-else-if="field.type === 'color'">
                            <label class="ind-field-label">{{ field.label }}</label>
                            <div class="ind-color-wrapper">
                                <input
                                    type="color"
                                    :value="localSettings[field.key]"
                                    class="ind-color-input"
                                    @input="onInputChange(field.key, $event.target.value)"
                                />
                                <span class="ind-color-hex">{{ localSettings[field.key] }}</span>
                            </div>
                        </template>

                        <!-- Date -->
                        <template v-else-if="field.type === 'date'">
                            <label class="ind-field-label">{{ field.label }}</label>
                            <input
                                type="date"
                                :value="localSettings[field.key]"
                                class="ind-text-input"
                                @input="onInputChange(field.key, $event.target.value)"
                            />
                        </template>

                        <!-- Text (default) -->
                        <template v-else>
                            <label class="ind-field-label">{{ field.label }}</label>
                            <input
                                type="text"
                                :value="localSettings[field.key]"
                                class="ind-text-input"
                                @input="onInputChange(field.key, $event.target.value)"
                            />
                        </template>
                    </div>
                    </template>
                </div>

                <!-- Style Tab -->
                <div v-show="activeTab === 'style'" class="ind-tab-content">
                    <div v-if="!plotStyles.length" class="ind-empty-state">
                        <i>No plot styles available</i>
                    </div>
                    <div v-for="(plot, i) in plotStyles" :key="i" class="ind-style-row">
                        <div class="ind-style-header">
                            <span class="ind-style-name">{{ plot.label || plot.key }}</span>
                        </div>
                        <div class="ind-style-controls">
                            <div class="ind-style-field">
                                <label>Color</label>
                                <input
                                    type="color"
                                    :value="plot.color"
                                    class="ind-color-input"
                                    @input="onStyleChange(i, 'color', $event.target.value)"
                                />
                            </div>
                            <div class="ind-style-field">
                                <label>Width</label>
                                <input
                                    type="number"
                                    :value="plot.width"
                                    min="0.5"
                                    max="10"
                                    step="0.5"
                                    class="ind-style-num"
                                    @input="onStyleChange(i, 'width', parseFloat($event.target.value))"
                                />
                            </div>
                            <div class="ind-style-field">
                                <label>Style</label>
                                <select
                                    :value="plot.style"
                                    class="ind-style-select"
                                    @change="onStyleChange(i, 'style', $event.target.value)">
                                    <option value="line">Line</option>
                                    <option value="dashed">Dashed</option>
                                    <option value="dotted">Dotted</option>
                                    <option value="histogram">Histogram</option>
                                    <option value="dots">Dots</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Visibility Tab -->
                <div v-show="activeTab === 'visibility'" class="ind-tab-content">
                    <div v-if="!isGroup" class="ind-input-row"><label class="ind-field-label">Minimum chart interval (minutes)</label><input aria-label="Minimum chart interval" class="ind-text-input" type="number" min="0" step="any" :value="(localSettings.$visibility?.minMs||0)/60000" @input="setVisibility('minMs',$event.target.value)" /></div>
                    <div v-if="!isGroup" class="ind-input-row"><label class="ind-field-label">Maximum chart interval (minutes; 0 = all)</label><input aria-label="Maximum chart interval" class="ind-text-input" type="number" min="0" step="any" :value="(localSettings.$visibility?.maxMs||0)/60000" @input="setVisibility('maxMs',$event.target.value)" /></div>

                    <div v-if="!plotStyles.length" class="ind-empty-state">
                        <i>No plots to configure visibility</i>
                    </div>
                    <div v-for="(plot, i) in plotStyles" :key="i" class="ind-visibility-row">
                        <label class="ind-vis-name"><input type="checkbox" :aria-label="'Show '+(plot.label||plot.key)" :checked="plot.visible!==false" @change="plot.visible=$event.target.checked" /> {{ plot.label || plot.key }}</label>
                        <div class="ind-vis-controls">
                            <label class="ind-vis-label">Opacity</label>
                            <input
                                type="range"
                                :value="getOpacity(i)"
                                min="0"
                                max="100"
                                step="5"
                                class="ind-vis-range"
                                @input="onOpacityChange(i, $event.target.value)"
                            />
                            <span class="ind-vis-value">{{ getOpacity(i) }}%</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Footer -->
            <div class="ind-settings-footer">
                <div class="ind-defaults-wrapper">
                    <button class="ind-defaults-btn" @click="toggleDefaultsMenu">
                        Defaults
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
                            <polyline points="6 9 12 15 18 9"/>
                        </svg>
                    </button>
                    <div v-if="showDefaultsMenu" class="ind-defaults-menu">
                        <button @click="resetToDefault">Reset to Default</button>
                        <button @click="saveAsDefault">Save as Default</button>
                    </div>
                </div>
                <div class="ind-footer-actions">
                    <button class="ind-btn ind-btn-cancel" @click="onCancel">Cancel</button>
                    <button class="ind-btn ind-btn-ok" @click="onOk">Ok</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Utils from './utils.js'
import ChartUtils from '../../stuff/utils.js'
import {indicatorSourceValue} from '../../workspace/indicator-sources.mjs'
import {timeframeMs} from '../../workspace/indicator-execution.mjs'
import { indicatorPlots } from '../../workspace/indicator-plots.mjs'
import {getRunner} from '../../candle-script/runner.js'
import { DEFAULT_VT_GROUP } from '../../services/indicatorGroups.js'

export default {
    name: 'IndicatorSettings',
    props: ['id', 'main', 'dc', 'tv', 'data'],
    data() {
        return {
            activeTab: 'inputs',
            tabs: [
                { id: 'inputs', label: 'Inputs' },
                { id: 'style', label: 'Style' },
                { id: 'visibility', label: 'Visibility' },
            ],
            localSettings: {},
            plotStyles: [],
            plotOpacities: [],
            showDefaultsMenu: false,
            originalSettings: null,
            overlaySnapshots: [],
            previewReady: false,
            originalPlotStyles: null,
            originalOpacities: null,
            // Group mode data
            groupInputFields: [],
            groupPlotStyles: [],
            groupPlotOpacities: [],
        }
    },
    mounted() {
        this.overlaySnapshots = (this.hasTimeframeTrend ? [...(this.dc.data.onchart||[]),...(this.dc.data.offchart||[])] : this.isGroup ? this.overlays : [this.ov]).map(ov => ({ ov, settings: JSON.parse(JSON.stringify(ov.settings || {})) }))
        this.initFromOverlay()
        this.$nextTick(() => {
            this.previewReady = true
            if(this.hasTimeframeTrend)window.dispatchEvent(new CustomEvent('timeframe-trend-properties',{detail:{target:this.$refs.timeframeTrendProperties}}))
        })
        document.addEventListener('click', this.closeDefaultsMenu)
    },
    beforeDestroy() {
        if(this.hasTimeframeTrend)window.dispatchEvent(new CustomEvent('timeframe-trend-properties',{detail:{target:null}}))
        document.removeEventListener('click', this.closeDefaultsMenu)
    },
    watch: {
        localSettings: { deep: true, handler() { if (this.previewReady) this.applySettings(false) } },
        plotStyles: { deep: true, handler() { if (this.previewReady) this.applySettings(false) } },
        plotOpacities: { deep: true, handler() { if (this.previewReady) this.applySettings(false) } },
    },
    computed: {
        hasTimeframeTrend(){return (this.isGroup?this.overlays:[this.ov]).some(ov=>ov?.type==='TimeframeTrend')},
        popoverStyle() {
            const anchor = this.data.anchor;
            if (!anchor) return {};
            const left = Math.max(8, Math.min(anchor.left, window.innerWidth- Math.min(420,window.innerWidth-16)-8));
            const top = Math.max(8, Math.min(anchor.bottom+8, window.innerHeight-Math.min(600,window.innerHeight*.8)-8));
            return {position:'fixed',left:left+'px',top:top+'px',transformOrigin: `${anchor.left-left+anchor.width/2}px ${anchor.top-top}px`};
        },
        isGroup() {
            return !!(this.$props.data && this.$props.data.isGroup)
        },
        ov() {
            return this.$props.data.ov
        },
        overlays() {
            return (this.$props.data && this.$props.data.overlays) || []
        },
        ovName() {
            if (this.isGroup) {
                return this.$props.data.groupName || 'Group Settings'
            }
            return ChartUtils.format_name({ ...this.ov, settings: { ...this.sett, ...this.localSettings } }) || this.ov.type || 'Indicator'
        },
        sett() {
            if (this.isGroup) return {}
            return this.ov.settings || {}
        },
        inputFields() {
            if(!this.isGroup && this.ov?.type==='TimeframeTrend')return []
            if (this.isGroup) {
                return this.groupInputFields
            }
            // Build input fields from $props or options metadata
            const fields = []
            const props = this.sett.$props || []
            const options = this.sett.$options || []

            if (options.length) {
                // Use structured options metadata if available
                for (const opt of options) {
                    fields.push({
                        key: opt.key,
                        label: opt.label || Utils.sett2desc(opt.key),
                        type: opt.type || 'number',
                        default: opt.default,
                        integer: opt.integer,
                        min: opt.min,
                        max: opt.max,
                        step: opt.step,
                        choices: opt.key==='source'?this.indicatorSourceChoices(this.ov,opt.choices||[]):opt.choices||[],
                    })
                }
            } else if (props.length) {
                // Fallback: infer types from current values
                // Exclude style-related keys (handled in Style tab)
                const styleKeys = ['color', 'lineWidth', 'lineStyle', 'opacity']
                for (const key of props) {
                    if (styleKeys.includes(key)) continue
                    const val = this.sett[key]
                    let type = 'text'
                    if (typeof val === 'number') type = 'number'
                    else if (typeof val === 'boolean') type = 'boolean'
                    else if (typeof val === 'string' && val.match(/^#[0-9a-fA-F]{3,8}$/)) type = 'color'

                    fields.push({
                        key,
                        label: Utils.sett2desc(key),
                        type,
                        default: val,
                    })
                }
            } else {
                // Last fallback: show all non-$ settings
                // Exclude style-related keys (handled in Style/Visibility tabs)
                const styleKeys = ['color', 'lineWidth', 'lineStyle', 'opacity']
                for (const key of Object.keys(this.sett)) {
                    if (key.startsWith('$')) continue
                    if (key === 'display' || key === 'z-index' || key === 'zIndex') continue
                    if (styleKeys.includes(key)) continue
                    const val = this.sett[key]
                    if (typeof val === 'function' || typeof val === 'object') continue

                    let type = 'text'
                    if (typeof val === 'number') type = 'number'
                    else if (typeof val === 'boolean') type = 'boolean'
                    else if (typeof val === 'string' && val.match(/^#[0-9a-fA-F]{3,8}$/)) type = 'color'

                    fields.push({
                        key,
                        label: Utils.sett2desc(key),
                        type,
                        default: val,
                    })
                }
            }
            return fields
        },
        tabIndicatorStyle() {
            const idx = this.tabs.findIndex(t => t.id === this.activeTab)
            const width = 100 / this.tabs.length
            return {
                width: width + '%',
                transform: `translateX(${idx * 100}%)`,
            }
        },
    },
    methods: {
        indicatorSourceChoices(overlay,base){
            const choices=[...base],chartMs=this.tv?.$refs?.chart?.interval_ms||60000;
            const selected=this.localSettings.executionTimeframe||overlay.settings?.executionTimeframe||'Chart';
            const interval=value=>!value||value==='Chart'?chartMs:timeframeMs(value);
            for(const source of this.dc.get('.')){
                if(source===overlay||!source.settings?.$uuid||!source.settings?.$props||source.settings.$synth||interval(source.settings.executionTimeframe)!==interval(selected))continue;
                const plots=indicatorPlots(source);
                for(let index=0;index<plots.length;index++)if(source.data?.some(row=>Number.isFinite(row[index+1])))choices.push({value:indicatorSourceValue(source.settings.$uuid,index+1),label:ChartUtils.format_name(source)+' · '+plots[index].label});
            }
            const current=this.localSettings.source||overlay.settings?.source;
            if(typeof current==='string'&&current.startsWith('indicator:')&&!choices.some(choice=>choice.value===current))choices.push({value:current,label:'Unavailable indicator output'});
            return choices;
        },

        setVisibility(key,value){const minutes=Number(value);if(!Number.isFinite(minutes)||minutes<0)return;const visibility={...this.localSettings.$visibility};if(key==='maxMs'&&minutes===0)delete visibility[key];else visibility[key]=minutes*60000;this.localSettings={...this.localSettings,$visibility:visibility}},
        initFromOverlay() {
            if (this.isGroup) {
                this.initFromGroup()
                return
            }
            // Clone current settings for local editing
            const settings = {}
            for (const field of this.inputFields) {
                settings[field.key] = this.sett[field.key] !== undefined
                    ? this.sett[field.key]
                    : field.default
            }
            if(this.sett.$visibility)settings.$visibility={...this.sett.$visibility}
            this.localSettings = settings

            // Initialize plot styles from plotConfig or settings
            const outputs = indicatorPlots(this.ov)

            if (outputs.length) {
                this.plotStyles = outputs.map(o => ({
                    key: o.key,
                    label: o.label || o.key,
                    color: o.color || '#2962ff',
                    width: o.width || 1.5,
                    style: o.style || 'line',
                    visible: o.visible !== false,
                }))
            } else {
                // Fallback: create a single plot from overlay settings
                this.plotStyles = [{
                    key: 'main',
                    label: this.ovName,
                    color: this.sett.color || '#2962ff',
                    width: this.sett.lineWidth || 1.5,
                    style: this.sett.lineStyle || 'line',
                }]
            }

            // Initialize opacities
            const savedOpacities = this.sett.$opacities || []
            this.plotOpacities = this.plotStyles.map((_, i) =>
                savedOpacities[i] !== undefined ? savedOpacities[i] : 100
            )

            // Store originals for cancel
            this.originalSettings = JSON.parse(JSON.stringify(this.localSettings))
            this.originalPlotStyles = JSON.parse(JSON.stringify(this.plotStyles))
            this.originalOpacities = [...this.plotOpacities]
        },

        initFromGroup() {
            // Combine inputs, styles, and visibility from all overlays in the group
            const allFields = []
            const allSettings = {}
            const allPlotStyles = []
            const allOpacities = []

            for (let oi = 0; oi < this.overlays.length; oi++) {
                const ov = this.overlays[oi]
                const sett = ov.settings || {}
                const ovName = ChartUtils.format_name(ov) || `Indicator ${oi + 1}`
                const prefix = `__ov${oi}__`

                // Build input fields for this overlay
                const props = sett.$props || []
                const options = sett.$options || []

                if (options.length) {
                    for (const opt of options) {
                        const key = prefix + opt.key
                        allFields.push({
                            key,
                            label: opt.label || Utils.sett2desc(opt.key),
                            type: opt.type || 'number',
                            default: opt.default,
                            integer: opt.integer,
                        min: opt.min,
                            max: opt.max,
                            step: opt.step,
                            choices: opt.key==='source'?this.indicatorSourceChoices(ov,opt.choices||[]):opt.choices||[],
                            _ovIndex: oi,
                            _ovName: ovName,
                            _originalKey: opt.key,
                        })
                        allSettings[key] = sett[opt.key] !== undefined ? sett[opt.key] : opt.default
                    }
                } else if (props.length) {
                    const styleKeys = ['color', 'lineWidth', 'lineStyle', 'opacity']
                    for (const k of props) {
                        if (styleKeys.includes(k)) continue
                        const val = sett[k]
                        const key = prefix + k
                        let type = 'text'
                        if (typeof val === 'number') type = 'number'
                        else if (typeof val === 'boolean') type = 'boolean'
                        else if (typeof val === 'string' && val.match(/^#[0-9a-fA-F]{3,8}$/)) type = 'color'

                        allFields.push({
                            key,
                            label: Utils.sett2desc(k),
                            type,
                            default: val,
                            _ovIndex: oi,
                            _ovName: ovName,
                            _originalKey: k,
                        })
                        allSettings[key] = val
                    }
                } else {
                    const styleKeys = ['color', 'lineWidth', 'lineStyle', 'opacity']
                    for (const k of Object.keys(sett)) {
                        if (k.startsWith('$')) continue
                        if (k === 'display' || k === 'z-index' || k === 'zIndex') continue
                        if (styleKeys.includes(k)) continue
                        const val = sett[k]
                        if (typeof val === 'function' || typeof val === 'object') continue

                        const key = prefix + k
                        let type = 'text'
                        if (typeof val === 'number') type = 'number'
                        else if (typeof val === 'boolean') type = 'boolean'
                        else if (typeof val === 'string' && val.match(/^#[0-9a-fA-F]{3,8}$/)) type = 'color'

                        allFields.push({
                            key,
                            label: Utils.sett2desc(k),
                            type,
                            default: val,
                            _ovIndex: oi,
                            _ovName: ovName,
                            _originalKey: k,
                        })
                        allSettings[key] = val
                    }
                }

                // Build plot styles for this overlay
                const outputs = indicatorPlots(ov)

                if (outputs.length) {
                    for (const o of outputs) {
                        allPlotStyles.push({
                            key: o.key,
                            label: `${ovName} — ${o.label || o.key}`,
                            color: o.color || '#2962ff',
                            width: o.width || 1.5,
                            style: o.style || 'line',
                            visible: o.visible !== false,
                            _ovIndex: oi,
                            _ovName: ovName,
                        })
                    }
                } else {
                    allPlotStyles.push({
                        key: 'main',
                        label: ovName,
                        color: sett.color || '#2962ff',
                        width: sett.lineWidth || 1.5,
                        style: sett.lineStyle || 'line',
                        _ovIndex: oi,
                        _ovName: ovName,
                    })
                }

                // Opacities
                const savedOpacities = sett.$opacities || []
                const ovOutputCount = outputs.length || 1
                for (let pi = 0; pi < ovOutputCount; pi++) {
                    allOpacities.push(savedOpacities[pi] !== undefined ? savedOpacities[pi] : 100)
                }
            }

            this.groupInputFields = allFields
            this.localSettings = allSettings
            this.plotStyles = allPlotStyles
            this.plotOpacities = allOpacities

            // Store originals for cancel
            this.originalSettings = JSON.parse(JSON.stringify(allSettings))
            this.originalPlotStyles = JSON.parse(JSON.stringify(allPlotStyles))
            this.originalOpacities = [...allOpacities]
        },

        parseNum(val) {
            const n = parseFloat(val)
            return isNaN(n) ? val : n
        },

        onInputChange(key, value) {
            const field = this.inputFields.find(field => field.key === key)
            if (field && field.type === 'number') {
                if (!Number.isFinite(value)) return
                if (field.integer && !Number.isInteger(value)) return
                if (field.min != null && value < field.min) return
                if (field.max != null && value > field.max) return
            }
            this.$set(this.localSettings, key, value)
        },

        onStyleChange(index, prop, value) {
            this.$set(this.plotStyles[index], prop, value)
        },

        getOpacity(index) {
            return this.plotOpacities[index] ?? 100
        },

        onOpacityChange(index, value) {
            this.$set(this.plotOpacities, index, parseInt(value))
        },

        toggleDefaultsMenu() {
            this.showDefaultsMenu = !this.showDefaultsMenu
        },

        closeDefaultsMenu(e) {
            if (!e.target.closest('.ind-defaults-wrapper')) {
                this.showDefaultsMenu = false
            }
        },

        resetToDefault() {
            this.showDefaultsMenu = false

            if (this.isGroup) {
                // Try to load saved group defaults
                const key = `ind-defaults-group-${this.$props.data.groupName}`
                const saved = localStorage.getItem(key)
                if (saved) {
                    try {
                        const defaults = JSON.parse(saved)
                        if (defaults.settings) this.localSettings = { ...defaults.settings }
                        if (defaults.plotStyles) this.plotStyles = JSON.parse(JSON.stringify(defaults.plotStyles))
                        if (defaults.opacities) this.plotOpacities = [...defaults.opacities]
                        return
                    } catch (e) { /* fall through */ }
                }
                // Reset to original values
                this.initFromGroup()
                return
            }

            // Try to load saved defaults from localStorage
            const key = `ind-defaults-${this.ov.type || this.ov.name}`
            const saved = localStorage.getItem(key)
            if (saved) {
                try {
                    const defaults = JSON.parse(saved)
                    if (defaults.settings) {
                        this.localSettings = { ...defaults.settings }
                    }
                    if (defaults.plotStyles) {
                        this.plotStyles = JSON.parse(JSON.stringify(defaults.plotStyles))
                    }
                    if (defaults.opacities) {
                        this.plotOpacities = [...defaults.opacities]
                    }
                    return
                } catch (e) { /* fall through */ }
            }

            // Otherwise reset to field defaults
            for (const field of this.inputFields) {
                if (field.default !== undefined) {
                    this.$set(this.localSettings, field.key, field.default)
                }
            }
            // Reset styles to original plotConfig
            this.initFromOverlay()
        },

        saveAsDefault() {
            this.showDefaultsMenu = false

            if (this.isGroup) {
                const key = `ind-defaults-group-${this.$props.data.groupName}`
                const defaults = {
                    settings: JSON.parse(JSON.stringify(this.localSettings)),
                    plotStyles: JSON.parse(JSON.stringify(this.plotStyles)),
                    opacities: [...this.plotOpacities],
                }
                localStorage.setItem(key, JSON.stringify(defaults))
                return
            }

            const key = `ind-defaults-${this.ov.type || this.ov.name}`
            const defaults = {
                settings: JSON.parse(JSON.stringify(this.localSettings)),
                plotStyles: JSON.parse(JSON.stringify(this.plotStyles)),
                opacities: [...this.plotOpacities],
            }
            localStorage.setItem(key, JSON.stringify(defaults))
        },

        applySettings(persist = true) {
            if (this.isGroup) {
                this.applyGroupSettings(persist)
                return
            }

            const dcid = this.ov.id
            // Build merged settings
            const merged = {}

            // Apply input values
            for (const key of Object.keys(this.localSettings)) {
                merged[key] = this.localSettings[key]
            }

            // Apply plot styles back to settings
            if (this.plotStyles.length === 1 && this.plotStyles[0].key === 'main') {
                // Single plot: apply directly to settings
                merged.color = this.plotStyles[0].color
                merged.lineWidth = this.plotStyles[0].width
                merged.lineStyle = this.plotStyles[0].style
            }

            // Store plotConfig and opacities in settings for persistence
            merged.$plotConfig = {
                outputs: JSON.parse(JSON.stringify(this.plotStyles))
            }
            merged.$opacities = [...this.plotOpacities]

            // Apply opacity directly to settings so overlays can read it
            // For single-plot overlays, use the first opacity value
            if (this.plotOpacities.length > 0) {
                merged.opacity = this.plotOpacities[0]
            }

            this.$props.dc.merge(`${dcid}.settings`, merged)
            if (persist) this.persistOverlayInSavedGroup(this.ov, merged)
        },

        applyGroupSettings(persist = true) {
            // Apply settings back to each overlay in the group
            let plotIdx = 0
            const savedIndicators = []

            for (let oi = 0; oi < this.overlays.length; oi++) {
                const ov = this.overlays[oi]
                const dcid = ov.id
                const prefix = `__ov${oi}__`
                const merged = {}

                // Apply input values for this overlay
                for (const key of Object.keys(this.localSettings)) {
                    if (key.startsWith(prefix)) {
                        const originalKey = key.slice(prefix.length)
                        merged[originalKey] = this.localSettings[key]
                    }
                }

                // Collect plot styles belonging to this overlay
                const ovPlots = this.plotStyles.filter(p => p._ovIndex === oi)
                const ovOpacities = []

                // Apply plot styles
                if (ovPlots.length === 1 && ovPlots[0].key === 'main') {
                    merged.color = ovPlots[0].color
                    merged.lineWidth = ovPlots[0].width
                    merged.lineStyle = ovPlots[0].style
                }

                // Build plotConfig outputs (strip internal metadata)
                const cleanPlots = ovPlots.map(p => ({
                    key: p.key,
                    label: p.label.includes(' — ') ? p.label.split(' — ')[1] : p.label,
                    color: p.color,
                    width: p.width,
                    style: p.style,
                }))

                // Collect opacities for this overlay's plots
                for (let pi = 0; pi < ovPlots.length; pi++) {
                    ovOpacities.push(this.plotOpacities[plotIdx + pi])
                }
                plotIdx += ovPlots.length

                merged.$plotConfig = { outputs: cleanPlots }
                merged.$opacities = ovOpacities

                // Apply opacity directly so overlays can read it
                if (ovOpacities.length > 0) {
                    merged.opacity = ovOpacities[0]
                }

                this.$props.dc.merge(`${dcid}.settings`, merged)
                savedIndicators.push(this.buildSavedIndicator(ov, merged))
            }
            if (persist) this.persistSavedGroup(this.$props.data.groupName, savedIndicators)
        },

        cleanSettings(settings) {
            const cleaned = {}
            for (const key of Object.keys(settings || {})) {
                if (key === '$uuid' || key === '$selected' || key === '$state' || key === '$last_exec') continue
                cleaned[key] = settings[key]
            }
            return JSON.parse(JSON.stringify(cleaned))
        },

        getOverlaySide(ov) {
            if (this.$props.data.side) return this.$props.data.side
            const dcData = this.$props.dc && this.$props.dc.data
            if (dcData && Array.isArray(dcData.onchart) && dcData.onchart.includes(ov)) return 'onchart'
            if (dcData && Array.isArray(dcData.offchart) && dcData.offchart.includes(ov)) return 'offchart'
            return 'onchart'
        },

        buildSavedIndicator(ov, mergedSettings) {
            const settings = this.cleanSettings({
                ...(ov.settings || {}),
                ...(mergedSettings || {}),
                group: this.$props.data.groupName || (ov.settings && ov.settings.group) || ov.group || 'Indicators'
            })
            return {
                name: ov.name || ov.type,
                type: ov.type,
                side: this.getOverlaySide(ov),
                hidden: settings.display === false,
                settings
            }
        },

        readSavedGroups() {
            try {
                return JSON.parse(localStorage.getItem('tc_saved_indicator_groups') || '[]')
            } catch (e) {
                return []
            }
        },

        writeSavedGroups(groups) {
            localStorage.setItem('tc_saved_indicator_groups', JSON.stringify(groups))
        },

        persistSavedGroup(groupName, indicators) {
            if (!groupName || !indicators || !indicators.length) return
            const groups = this.readSavedGroups()
            const index = groups.findIndex(g => g.name === groupName || (groupName === 'VT' && g.id === 'default_vt'))
            const existing = index >= 0 ? groups[index] : {}
            const saved = {
                ...existing,
                id: existing.id || (groupName === 'VT' ? 'default_vt' : 'ig_' + Date.now()),
                name: groupName,
                description: existing.description || (groupName === 'VT' ? 'Visual Trading indicator set' : ''),
                indicators,
                isDefault: existing.isDefault || groupName === 'VT',
                createdAt: existing.createdAt || new Date().toISOString(),
                updatedAt: new Date().toISOString()
            }
            if (index >= 0) {
                groups.splice(index, 1, saved)
            } else {
                groups.push(saved)
            }
            this.writeSavedGroups(groups)
        },

        persistOverlayInSavedGroup(ov, mergedSettings) {
            const groupName = (ov.settings && ov.settings.group) || ov.group
            if (!groupName) return
            const groups = this.readSavedGroups()
            const group = groups.find(g => g.name === groupName || (groupName === 'VT' && g.id === 'default_vt'))
            if (!group && groupName !== 'VT') return

            const saved = this.buildSavedIndicator(ov, mergedSettings)
            const indicators = group && Array.isArray(group.indicators)
                ? [...group.indicators]
                : (groupName === 'VT' ? JSON.parse(JSON.stringify(DEFAULT_VT_GROUP.indicators)) : [])
            const index = indicators.findIndex(ind => ind.type === saved.type || ind.name === saved.name)
            if (index >= 0) {
                indicators.splice(index, 1, saved)
            } else {
                indicators.push(saved)
            }
            this.persistSavedGroup(groupName, indicators)
        },

        onOk() {
            this.previewReady = false
            this.applySettings()
            this.close()
        },

        onCancel() {
            this.previewReady = false
            for (const { ov, settings } of this.overlaySnapshots) {
                this.$props.dc.set(`${ov.id}.settings`, settings)
                if(this.hasTimeframeTrend && settings.candleScriptId)getRunner().setSignalConfig(settings.candleScriptId,settings.signalConfig)
            }
            this.close()
        },

        close() {
            this.$props.main.remove_widget(this.$props.id)
        },
    },
}
</script>

<style scoped>
.ind-settings-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    pointer-events: all;
}

.ind-settings-dialog {
    background: #fff;
    border-radius: 8px;
    width: 420px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1);
    overflow: hidden;
}

/* Dark theme */
.chart-page-dark .ind-settings-dialog {
    background: #1e222d;
    border: 1px solid rgba(55, 59, 75, 0.6);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
}

/* Header */
.ind-settings-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px 12px;
}

.ind-settings-title {
    font-size: 20px;
    font-weight: 600;
    margin: 0;
    color: #131722;
}
.chart-page-dark .ind-settings-title {
    color: #d1d4dc;
}

.ind-settings-close {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    color: #787b86;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
}
.ind-settings-close:hover {
    color: #131722;
    background: rgba(0, 0, 0, 0.05);
}
.chart-page-dark .ind-settings-close:hover {
    color: #d1d4dc;
    background: rgba(255, 255, 255, 0.05);
}

/* Tabs */
.ind-settings-tabs {
    display: flex;
    padding: 0 24px;
    gap: 0;
}

.ind-tab-btn {
    flex: 1;
    background: none;
    border: none;
    padding: 10px 16px;
    font-size: 14px;
    font-weight: 600;
    color: #787b86;
    cursor: pointer;
    transition: color 0.2s;
    text-align: center;
}
.ind-tab-btn:hover {
    color: #131722;
}
.ind-tab-btn.active {
    color: #131722;
}
.chart-page-dark .ind-tab-btn:hover,
.chart-page-dark .ind-tab-btn.active {
    color: #d1d4dc;
}

.ind-tab-line {
    position: relative;
    height: 2px;
    background: #e0e3eb;
    margin: 0 24px;
}
.chart-page-dark .ind-tab-line {
    background: #363a45;
}

.ind-tab-indicator {
    position: absolute;
    bottom: 0;
    left: 0;
    height: 2px;
    background: #131722;
    transition: transform 0.25s ease, width 0.25s ease;
}
.chart-page-dark .ind-tab-indicator {
    background: #d1d4dc;
}

/* Body */
.ind-settings-body {
    flex: 1;
    overflow-y: auto;
    padding: 16px 24px;
    min-height: 200px;
    max-height: 400px;
}

.ind-tab-content {
    animation: fadeIn 0.15s ease;
}

@keyframes fadeIn {
    from { opacity: 0; transform: translateY(4px); }
    to { opacity: 1; transform: translateY(0); }
}

.ind-empty-state {
    text-align: center;
    padding: 32px;
    color: #787b86;
    font-size: 13px;
}

/* Group section header (for combined group settings) */
.ind-group-section-header {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #787b86;
    padding: 14px 0 6px;
    border-bottom: 1px solid #e0e3eb;
    margin-bottom: 4px;
}
.ind-group-section-header:first-child {
    padding-top: 0;
}
.chart-page-dark .ind-group-section-header {
    color: #9ca3af;
    border-bottom-color: #2a2e39;
}

/* Input Fields */
.ind-input-row {
    display: flex;
    align-items: center;
    padding: 12px 0;
    border-bottom: 1px solid #f0f3fa;
}
.ind-input-row:last-child {
    border-bottom: none;
}
.chart-page-dark .ind-input-row {
    border-bottom-color: #2a2e39;
}

.ind-field-label {
    flex: 1;
    font-size: 14px;
    color: #131722;
    font-weight: 400;
}
.chart-page-dark .ind-field-label {
    color: #d1d4dc;
}

.ind-text-input {
    width: 120px;
    padding: 8px 12px;
    border: 1px solid #e0e3eb;
    border-radius: 4px;
    font-size: 14px;
    color: #131722;
    background: #fff;
    outline: none;
    transition: border-color 0.15s;
}
.ind-text-input:focus {
    border-color: #131722;
}
.chart-page-dark .ind-text-input {
    background: #2a2e39;
    border-color: #363a45;
    color: #d1d4dc;
}
.chart-page-dark .ind-text-input:focus {
    border-color: #5b6178;
}

.ind-select-input {
    width: 140px;
    padding: 8px 12px;
    border: 1px solid #e0e3eb;
    border-radius: 4px;
    font-size: 14px;
    color: #131722;
    background: #fff;
    outline: none;
    cursor: pointer;
    -webkit-appearance: none;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23787b86' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 8px center;
    padding-right: 28px;
}
.chart-page-dark .ind-select-input {
    background-color: #2a2e39;
    border-color: #363a45;
    color: #d1d4dc;
}

/* Checkbox */
.ind-checkbox-row {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    width: 100%;
}

.ind-checkbox {
    width: 18px;
    height: 18px;
    accent-color: #131722;
    cursor: pointer;
}

.ind-checkbox-label {
    font-size: 14px;
    color: #131722;
}
.chart-page-dark .ind-checkbox-label {
    color: #d1d4dc;
}

/* Color input */
.ind-color-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
}

.ind-color-input {
    width: 32px;
    height: 32px;
    border: 1px solid #e0e3eb;
    border-radius: 4px;
    padding: 2px;
    cursor: pointer;
    background: none;
}
.chart-page-dark .ind-color-input {
    border-color: #363a45;
}

.ind-color-hex {
    font-size: 12px;
    color: #787b86;
    font-family: 'SF Mono', 'Fira Code', monospace;
}

/* Style Tab */
.ind-style-row {
    padding: 14px 0;
    border-bottom: 1px solid #f0f3fa;
}
.ind-style-row:last-child {
    border-bottom: none;
}
.chart-page-dark .ind-style-row {
    border-bottom-color: #2a2e39;
}

.ind-style-header {
    margin-bottom: 10px;
}

.ind-style-name {
    font-size: 14px;
    font-weight: 500;
    color: #131722;
}
.chart-page-dark .ind-style-name {
    color: #d1d4dc;
}

.ind-style-controls {
    display: flex;
    align-items: center;
    gap: 16px;
}

.ind-style-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.ind-style-field label {
    font-size: 11px;
    color: #787b86;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.ind-style-num {
    width: 60px;
    padding: 6px 8px;
    border: 1px solid #e0e3eb;
    border-radius: 4px;
    font-size: 13px;
    color: #131722;
    background: #fff;
    outline: none;
}
.chart-page-dark .ind-style-num {
    background: #2a2e39;
    border-color: #363a45;
    color: #d1d4dc;
}

.ind-style-select {
    padding: 6px 8px;
    border: 1px solid #e0e3eb;
    border-radius: 4px;
    font-size: 13px;
    color: #131722;
    background: #fff;
    outline: none;
    cursor: pointer;
}
.chart-page-dark .ind-style-select {
    background: #2a2e39;
    border-color: #363a45;
    color: #d1d4dc;
}

/* Visibility Tab */
.ind-visibility-row {
    display: flex;
    align-items: center;
    padding: 14px 0;
    border-bottom: 1px solid #f0f3fa;
}
.ind-visibility-row:last-child {
    border-bottom: none;
}
.chart-page-dark .ind-visibility-row {
    border-bottom-color: #2a2e39;
}

.ind-vis-name {
    min-width: 100px;
    font-size: 14px;
    font-weight: 500;
    color: #131722;
}
.chart-page-dark .ind-vis-name {
    color: #d1d4dc;
}

.ind-vis-controls {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
}

.ind-vis-label {
    font-size: 12px;
    color: #787b86;
    min-width: 50px;
}

.ind-vis-range {
    flex: 1;
    height: 4px;
    -webkit-appearance: none;
    appearance: none;
    background: #e0e3eb;
    border-radius: 2px;
    outline: none;
}
.chart-page-dark .ind-vis-range {
    background: #363a45;
}
.ind-vis-range::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #131722;
    cursor: pointer;
}
.chart-page-dark .ind-vis-range::-webkit-slider-thumb {
    background: #d1d4dc;
}

.ind-vis-value {
    font-size: 12px;
    color: #787b86;
    min-width: 36px;
    text-align: right;
}

/* Footer */
.ind-settings-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 24px;
    border-top: 1px solid #e0e3eb;
}
.chart-page-dark .ind-settings-footer {
    border-top-color: #2a2e39;
}

.ind-defaults-wrapper {
    position: relative;
}

.ind-defaults-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border: 1px solid #e0e3eb;
    border-radius: 4px;
    background: #fff;
    font-size: 13px;
    color: #131722;
    cursor: pointer;
    transition: all 0.15s;
}
.ind-defaults-btn:hover {
    border-color: #131722;
}
.chart-page-dark .ind-defaults-btn {
    background: #2a2e39;
    border-color: #363a45;
    color: #d1d4dc;
}
.chart-page-dark .ind-defaults-btn:hover {
    border-color: #5b6178;
}

.ind-defaults-menu {
    position: absolute;
    bottom: 100%;
    left: 0;
    margin-bottom: 4px;
    background: #fff;
    border: 1px solid #e0e3eb;
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    z-index: 10;
    min-width: 160px;
}
.chart-page-dark .ind-defaults-menu {
    background: #2a2e39;
    border-color: #363a45;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
}

.ind-defaults-menu button {
    display: block;
    width: 100%;
    padding: 10px 14px;
    border: none;
    background: none;
    font-size: 13px;
    color: #131722;
    text-align: left;
    cursor: pointer;
}
.ind-defaults-menu button:hover {
    background: #f0f3fa;
}
.chart-page-dark .ind-defaults-menu button {
    color: #d1d4dc;
}
.chart-page-dark .ind-defaults-menu button:hover {
    background: #363a45;
}

.ind-footer-actions {
    display: flex;
    gap: 8px;
}

.ind-btn {
    padding: 8px 20px;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s;
}

.ind-btn-cancel {
    background: #fff;
    border: 1px solid #e0e3eb;
    color: #131722;
}
.ind-btn-cancel:hover {
    border-color: #131722;
}
.chart-page-dark .ind-btn-cancel {
    background: #2a2e39;
    border-color: #363a45;
    color: #d1d4dc;
}
.chart-page-dark .ind-btn-cancel:hover {
    border-color: #5b6178;
}

.ind-btn-ok {
    background: #131722;
    border: 1px solid #131722;
    color: #fff;
}
.ind-btn-ok:hover {
    background: #2a2e39;
}
.chart-page-dark .ind-btn-ok {
    background: #d1d4dc;
    border-color: #d1d4dc;
    color: #131722;
}
.chart-page-dark .ind-btn-ok:hover {
    background: #b2b5be;
}
</style>

<style>
.ind-settings-overlay{background:transparent;z-index:10020}
.ind-settings-dialog{background:rgba(250,252,255,.85);backdrop-filter:blur(24px);width:min(420px,calc(100vw - 16px));animation:tc-properties-in .2s cubic-bezier(.2,.8,.2,1)}
.chart-page-dark .ind-settings-dialog{background:rgba(25,32,48,.86)}
@keyframes tc-properties-in{from{opacity:0;transform:scale(.88) translateY(-8px)}to{opacity:1;transform:none}}
@media(prefers-reduced-motion:reduce){.ind-settings-dialog{animation:none}}
</style>
